import { IconButton, PrimaryButton, Stack, TeachingBubble } from "@fluentui/react";
import { CoObjectItem } from "../../interfaces/CoObject";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import React from "react";
import { FullPageModal } from "../../layout/FullPageModal";
import { Toggle } from "@fluentui/react/lib/Toggle";
import FooterPanel from "../../layout/FooterPanel";
import { UpdateOptions } from "../../../api/object/RefreshObject";
import { launchRefreshElaboration } from "../../../utils/helpers/ElaborationHelper";
import { ButtonVariants } from "../../../styles/fluent/ButtonVariants";
import { useDocumentPartListUpdateFooter } from "./DocumentPartListUpdateFooterHook";
import { DocumentPart } from "../../../api/models";
import { DocumentPartMetadata } from "../../../api/models/document-part-metadata";

export const DocumentPartListUpdateFooter = (props: {
  selectedObjects: Map<string, CoObjectItem>;
  username: string;
  documentPart: DocumentPart;
  documentPartMetadata: DocumentPartMetadata;
  isNotAuthorized: boolean;
}) => {
  const { documentPart, documentPartMetadata } = props;
  const {
    state,
    selectedObjects,
    infoId,
    disabled,
    updateOptions,
    username,
    setSelectedUpdateOption,
    setObjectUpdateOption,
    setTeachingBubbleVisible,
    setTableUpdateStrategy,
    setModalVisible,
    toggleModal,
  } = useDocumentPartListUpdateFooter(props);

  return (
    <>
      {state.modalVisible && (
        <FullPageModal
          modalProps={{ isOpen: state.modalVisible }}
          onClose={() => setModalVisible(false)}
          textTitle={formatMessage("cof.documentpartupdate.getchangesandupdate.updateoptions")}
        >
          <div
            style={{
              fontWeight: "500",
              padding: "2px 10px",
              fontSize: "1.05rem",
              color: "var(--color-grey)",
            }}
          >
            <div
              dangerouslySetInnerHTML={{ __html: formatMessage("cof.updates.getchangesandupdate.updateoptions.info") }}
            />
          </div>
          <Stack horizontal style={{ padding: "8px 10px" }}>
            <span style={{ height: "100%", textAlign: "start", flexGrow: 1, float: "left" }}>
              {formatMessage("cof.documentpartupdate.getchangesandupdate.option.includedataupdate")}
            </span>
            <Toggle
              id="include_data_toggle"
              checked={state.objectUpdateOption === "DATAFULL"}
              onChange={() => {
                state.objectUpdateOption === "DATAFULL"
                  ? setObjectUpdateOption("DATALESS")
                  : setObjectUpdateOption("DATAFULL");

                setTableUpdateStrategy({
                  ...state.tableUpdateStrategy,
                  overrideData: false,
                  overrideStyle: false,
                });
              }}
              style={{ float: "right" }}
              styles={{ root: { marginBottom: 0 } }}
            />
          </Stack>
          <Stack horizontal style={{ padding: "8px 10px" }}>
            <span style={{ height: "100%", textAlign: "start", flexGrow: 1, float: "left" }}>
              {formatMessage("cof.documentpartupdate.getchangesandupdate.option.enablefullobjectreset")}
            </span>
            <Toggle
              id="full_reset_toggle"
              disabled={state.objectUpdateOption === "DATALESS"}
              checked={state.tableUpdateStrategy.overrideData}
              onChange={(_, opt) => {
                setTableUpdateStrategy({
                  ...state.tableUpdateStrategy,
                  typeUpdate: opt === true ? "fullReset" : state.tableUpdateStrategy.typeUpdate,
                  overrideData: !state.tableUpdateStrategy.overrideData,
                  overrideStyle: state.tableUpdateStrategy.overrideStyle
                    ? state.tableUpdateStrategy.overrideData
                    : false,
                });
              }}
              style={{ float: "right" }}
              styles={{ root: { marginBottom: 0 } }}
            />
          </Stack>
          <Stack horizontal style={{ padding: "8px 10px" }}>
            <span style={{ height: "100%", textAlign: "start", flexGrow: 1, float: "left" }}>
              {formatMessage("cof.documentpartupdate.getchangesandupdate.option.keepalloverrides")}
            </span>
            <Toggle
              id="keep_all_override_toggle"
              checked={state.tableUpdateStrategy.overrideStyle}
              onChange={(_, opt) => {
                setTableUpdateStrategy({
                  ...state.tableUpdateStrategy,
                  typeUpdate: opt ? "keepAllChanges" : state.tableUpdateStrategy.typeUpdate,
                  overrideStyle: !state.tableUpdateStrategy.overrideStyle,
                  overrideData: state.tableUpdateStrategy.overrideData
                    ? state.tableUpdateStrategy.overrideStyle
                    : false,
                });
              }}
              style={{ float: "right" }}
              styles={{ root: { marginBottom: 0 } }}
              disabled={state.objectUpdateOption === "DATALESS"}
            />
          </Stack>
          <div style={{ padding: "10px" }}>
            <FooterPanel>
              <PrimaryButton
                text={formatMessage("cof.documentpartupdate.button.update")}
                styles={{
                  label: {
                    whiteSpace: "nowrap",
                  },
                  root: { float: "right" },
                }}
                onClick={() => {
                  const updateOptions: UpdateOptions = {
                    depthLevel: state.selectedUpdateOption.depthLevel,
                    objectUpdateOption: state.objectUpdateOption,
                  };
                  const coObjects = Array.from(selectedObjects.values()).map((coObj) => ({
                    ...coObj,
                  }));
                  launchRefreshElaboration(
                    username,
                    coObjects,
                    documentPart,
                    documentPartMetadata,
                    updateOptions,
                    state.tableUpdateStrategy.typeUpdate
                  );
                }}
              />
            </FooterPanel>
          </div>
        </FullPageModal>
      )}
      {state.teachingBubbleVisible && (
        <TeachingBubble
          calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
          target={`#${infoId}`}
          hasCloseButton={true}
          closeButtonAriaLabel="Close"
          onDismiss={() => setTeachingBubbleVisible(false)}
          headline={formatMessage("cof.documentpartupdate.teachingbubble.headline")}
        >
          <ul>
            <li>
              <b>{formatMessage("cof.documentpartupdate.teachingbubble.getchanges.title")}</b>:
              {formatMessage("cof.documentpartupdate.teachingbubble.getchanges.description")}
            </li>
            <li>
              <b>{formatMessage("cof.documentpartupdate.teachingbubble.getchangesfromlowest.title")}</b>:
              {formatMessage("cof.documentpartupdate.teachingbubble.getchangesfromlowest.description")}
            </li>
          </ul>
        </TeachingBubble>
      )}
      <div style={{ display: "flex", alignContent: "center", gap: "8px", float: "right" }}>
        <IconButton
          id={infoId}
          aria-label="Info"
          iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
          disabled={disabled}
          onClick={() => setTeachingBubbleVisible(!state.teachingBubbleVisible)}
        />
        <PrimaryButton
          id="get_changes_button"
          className={ButtonVariants.primary}
          text={state.selectedUpdateOption.text}
          styles={{
            label: {
              whiteSpace: "nowrap",
            },
            root: { float: "right" },
          }}
          disabled={disabled}
          split
          splitButtonMenuProps={{ id: "get_changes_split_button" }}
          menuProps={{
            items: updateOptions,
            isBeakVisible: false,
            onItemClick: setSelectedUpdateOption,
          }}
          onClick={() => {
            const updateOptions: UpdateOptions = {
              depthLevel: state.selectedUpdateOption.depthLevel,
            };
            state.selectedUpdateOption.depthLevel === "SHALLOWEST"
              ? launchRefreshElaboration(
                  username,
                  Array.from(selectedObjects.values()),
                  documentPart,
                  documentPartMetadata,
                  updateOptions
                )
              : toggleModal();
          }}
        />
      </div>
    </>
  );
};
